import { useMemo, useState } from 'react';
import { Loader2, Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import {
    usePayers,
    useCreatePayer,
    useUpdatePayer,
    useDeletePayer,
    type Payer,
    type PayerWritePayload,
    type SubmissionMethod,
} from '@/api/payers';
import { useIsOaConfigured } from '@/api/settings';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { FormField, Input } from '@/components/ui/FormField';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
// Configuration → Billing → Payers. The catalog of insurance companies (plus
// self-pay) this practice bills to. Payer records feed the client insurance
// pickers, claim payer lookups, and the Billing module's payer filter.
// Payer-specific allowable rates live on Session Types, not here.
//
// submissionMethod drives how claims on this payer are billed:
//   - Manual      → practice sends on paper/portal; Billing shows a
//                   "Mark as Submitted" action.
//   - Electronic  → 837P via Office Ally; requires OA credentials to be
//                   configured under Settings → Billing → Office Ally.

type StatusTab = 'Active' | 'Inactive' | 'All';
const STATUS_TABS: StatusTab[] = ['Active', 'Inactive', 'All'];

const SUBMISSION_OPTIONS: SubmissionMethod[] = ['Manual', 'Electronic'];
// 'Self-Pay' is intentionally omitted — every practice is provisioned with a
// system Self-Pay payer (`is_system: true`, locked from rename/delete). The
// server rejects POST/PATCH /payers when type === 'Self-Pay' so user-created
// payers can't impersonate the system row that claim auto-create keys
// bill_to='Client' on.
const TYPE_OPTIONS = ['Commercial', 'Medicaid', 'Medicare', 'TRICARE', 'Other'];

export function PayersPanel() {
    const { data, isLoading, isError, error } = usePayers();
    const createM = useCreatePayer();
    const updateM = useUpdatePayer();
    const deleteM = useDeletePayer();

    const [tab, setTab] = useState<StatusTab>('Active');
    const [editing, setEditing] = useState<Payer | null>(null);
    const [creating, setCreating] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState<Payer | null>(null);

    const rows = useMemo(() => {
        if (!data) return [];
        const filtered = tab === 'All' ? data : data.filter((r) => r.status === tab);
        return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    }, [data, tab]);

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <h2 className="font-display text-lg font-bold text-text">Payers</h2>
                <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                    <Plus className="w-3.5 h-3.5" /> Add payer
                </Button>
            </div>
            <p className="text-xs text-text-subtle mb-3">
                Insurance companies and payers this practice bills to. Allowable rates are
                configured per Session Type — not here.
            </p>

            <Card className="p-0 overflow-hidden">
                <div className="flex flex-wrap items-center gap-3 p-3 border-b border-border">
                    <div role="tablist" aria-label="Filter payers by status" className="flex items-center gap-1 p-0.5 bg-surface-muted rounded-md">
                        {STATUS_TABS.map((s) => (
                            <button
                                key={s}
                                type="button"
                                role="tab"
                                aria-selected={tab === s}
                                onClick={() => setTab(s)}
                                className={cn(
                                    'px-3 py-1 text-xs rounded transition-colors',
                                    tab === s ? 'bg-surface text-text shadow-xs font-medium' : 'text-text-muted hover:text-text',
                                )}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <div className="ml-auto text-xs text-text-subtle tabular-nums">
                        {rows.length} {rows.length === 1 ? 'payer' : 'payers'}
                    </div>
                </div>

                {isLoading ? (
                    <div className="p-12 flex items-center justify-center text-text-subtle">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-error">
                        <p className="font-medium mb-1">Couldn't load payers</p>
                        <p className="text-xs text-text-subtle">{extractErrorMessage(error, 'Please try again.')}</p>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-text-subtle">
                        <p>{data && data.length > 0 ? 'No payers match the current filter.' : 'No payers yet.'}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-3 py-2 font-bold">Name</th>
                                    <th className="px-3 py-2 font-bold w-32">Payer ID</th>
                                    <th className="px-3 py-2 font-bold w-32">Type</th>
                                    <th className="px-3 py-2 font-bold w-32">Submission</th>
                                    <th className="px-3 py-2 font-bold w-24">Status</th>
                                    <th className="px-3 py-2 font-bold w-24 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-surface-muted transition-colors">
                                        <td className="px-3 py-2 font-medium">
                                            <span className="inline-flex items-center gap-1.5">
                                                {r.name}
                                                {r.isSystem && (
                                                    <Lock
                                                        className="w-3 h-3 text-text-subtle"
                                                        aria-label="System payer — name and type are locked"
                                                    />
                                                )}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 font-mono text-xs text-text-muted">{r.payerId || '—'}</td>
                                        <td className="px-3 py-2 text-text-muted">{r.type || '—'}</td>
                                        <td className="px-3 py-2 text-text-muted">{r.submissionMethod || '—'}</td>
                                        <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                                        <td className="px-3 py-2 text-right">
                                            <Button variant="ghost" size="xs" aria-label={`Edit ${r.name}`} onClick={() => setEditing(r)} className="mr-1">
                                                <Pencil className="w-3 h-3" />
                                            </Button>
                                            {!r.isSystem && (
                                                <Button variant="ghost" size="xs" aria-label={`Delete ${r.name}`} onClick={() => setConfirmDelete(r)}>
                                                    <Trash2 className="w-3 h-3" />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {(creating || editing) && (
                <PayerDialog
                    initial={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSubmit={async (payload) => {
                        if (editing) await updateM.mutateAsync({ id: editing.id, payload });
                        else await createM.mutateAsync(payload);
                    }}
                />
            )}

            {confirmDelete && (
                <DeleteConfirmDialog
                    row={confirmDelete}
                    onClose={() => setConfirmDelete(null)}
                    onConfirm={async () => {
                        await deleteM.mutateAsync(confirmDelete.id);
                        setConfirmDelete(null);
                    }}
                />
            )}
        </div>
    );
}

function PayerDialog({
    initial,
    onClose,
    onSubmit,
}: {
    initial: Payer | null;
    onClose: () => void;
    onSubmit: (p: PayerWritePayload) => Promise<void>;
}) {
    const [name, setName]             = useState(initial?.name ?? '');
    const [payerId, setPayerId]       = useState(initial?.payerId ?? '');
    const [type, setType]             = useState(initial?.type ?? '');
    const [submission, setSubmission] = useState<SubmissionMethod>(initial?.submissionMethod ?? '');
    const [status, setStatus]         = useState<'Active' | 'Inactive'>(initial?.status ?? 'Active');
    const [saving, setSaving]         = useState(false);
    const [err, setErr]               = useState<string | null>(null);
    const locked                      = !!initial?.isSystem;
    const oaConfigured                = useIsOaConfigured();
    const startedElectronic           = initial?.submissionMethod === 'Electronic';

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!name.trim()) { setErr('Name is required'); return; }
        setSaving(true);
        try {
            // Omit name + type on system payers so the PATCH doesn't trip the
            // server's system-locked guards even if values match.
            const payload: PayerWritePayload = {
                payerId: payerId.trim(),
                submissionMethod: submission,
                status,
            };
            if (!locked) {
                payload.name = name.trim();
                payload.type = type.trim();
            }
            await onSubmit(payload);
            onClose();
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{initial ? 'Edit payer' : 'Add payer'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    {locked && (
                        <p className="mb-3 text-xs text-text-subtle bg-surface-muted rounded-md px-3 py-2">
                            <Lock className="inline w-3 h-3 mr-1 align-[-2px]" />
                            This is a system payer. Name and type are locked; other fields are editable.
                        </p>
                    )}
                    <FormField label="Name" required error={err ?? undefined} hint={locked ? 'Locked on system payers' : undefined}>
                        {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} disabled={locked} />}
                    </FormField>
                    <div className="grid grid-cols-2 gap-3">
                        <FormField label="Payer ID (EDI)">
                            {(id) => <Input id={id} value={payerId} onChange={(e) => setPayerId(e.target.value)} />}
                        </FormField>
                        <FormField label="Type" hint={locked ? 'Locked on system payers' : undefined}>
                            {(id) => (
                                <select
                                    id={id}
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                    disabled={locked}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <option value="">—</option>
                                    {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            )}
                        </FormField>
                        <FormField
                            label="Submission method"
                            hint={
                                !oaConfigured
                                    ? 'Electronic requires Office Ally credentials — configure them under Settings → Billing.'
                                    : undefined
                            }
                        >
                            {(id) => (
                                <select
                                    id={id}
                                    value={submission}
                                    onChange={(e) => setSubmission(e.target.value as SubmissionMethod)}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                >
                                    <option value="">—</option>
                                    {SUBMISSION_OPTIONS.map((s) => (
                                        <option
                                            key={s}
                                            value={s}
                                            // Only disable Electronic when OA isn't configured AND the payer
                                                // wasn't already on Electronic — otherwise we'd force the user to
                                                // change methods on an existing configured payer.
                                            disabled={s === 'Electronic' && !oaConfigured && !startedElectronic}
                                        >
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </FormField>
                        <FormField label="Status">
                            {(id) => (
                                <select
                                    id={id}
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value as 'Active' | 'Inactive')}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                >
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                </select>
                            )}
                        </FormField>
                    </div>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button variant="primary" size="sm" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : initial ? 'Save' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function DeleteConfirmDialog({
    row,
    onClose,
    onConfirm,
}: {
    row: Payer;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Delete payer?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-text-muted">
                    Delete <span className="font-medium text-text">{row.name}</span>? If this
                    payer is in use on any client or claim the delete will be refused — set
                    the payer to Inactive instead.
                </p>
                {err && <p className="mt-2 text-xs text-error">{err}</p>}
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="danger"
                        size="sm"
                        disabled={working}
                        onClick={async () => {
                            setErr(null);
                            setWorking(true);
                            try { await onConfirm(); }
                            catch (e) { setErr(extractErrorMessage(e, 'Could not delete.')); }
                            finally { setWorking(false); }
                        }}
                    >
                        {working ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
